import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Save, Rocket, RefreshCw, EyeOff, Archive, Loader2, CheckCircle2, AlertTriangle, Eye } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CHANNELS = [
  { key: "qru_online", label: "QRU Online" },
  { key: "shopify", label: "Shopify" },
  { key: "etsy", label: "Etsy" },
  { key: "youtube", label: "YouTube" },
];

export default function AdminProduct() {
  const { productKey } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({});
  const [sel, setSel] = useState(["qru_online"]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/products/${productKey}`).then((r) => {
    setP(r.data);
    setForm({ title: r.data.title, subtitle: r.data.subtitle || "", description: r.data.description || "", price: r.data.price, category: r.data.category, tags: (r.data.tags || []).join(", ") });
  }).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [productKey]);

  if (!p) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/products/${productKey}`, {
        title: form.title, subtitle: form.subtitle, description: form.description,
        price: parseFloat(form.price), category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Draft saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/${productKey}/publish`, { channels: sel });
      toast.success(`Published — ${data.lifecycle_status}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); } finally { setBusy(false); }
  };

  const retry = async (channel) => {
    try { await api.post(`/admin/products/${productKey}/retry`, { channel }); toast.success(`Retried ${channel}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
  };
  const unpublish = async () => {
    try { await api.post(`/admin/products/${productKey}/unpublish`, { channels: null }); toast.success("Unpublished"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const archive = async () => {
    try { await api.post(`/admin/products/${productKey}/archive`); toast.success("Archived"); nav("/admin"); }
    catch (e) { toast.error("Failed"); }
  };

  const valid = p.validation?.valid;
  const CH_COLOR = { published: "text-green-600", manual_action_required: "text-amber-600", failed: "text-red-600", pending: "text-slate-400", unpublished: "text-slate-400" };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-[#0A0F1D] text-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center">
          <Link to="/admin" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white"><ChevronLeft className="w-4 h-4" /> Catalog</Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        {/* Editor */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[#0A0F1D]">Draft Editor</h2>
              <span className="text-xs text-slate-400">v{p.package_version} · {p.lifecycle_status}</span>
            </div>
            {!valid && (
              <div className="mb-4 flex items-start gap-2 bg-red-50 text-red-700 text-sm p-3 rounded-lg">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div><p className="font-semibold">Validation errors</p><ul className="list-disc pl-4 mt-1">{p.validation?.errors?.map((e, i) => <li key={i}>{e}</li>)}</ul></div>
              </div>
            )}
            <div className="grid gap-4">
              <Field label="Title"><input data-testid="edit-title" className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field label="Subtitle"><input data-testid="edit-subtitle" className="inp" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price ($)"><input data-testid="edit-price" type="number" step="0.01" className="inp" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
                <Field label="Category"><input data-testid="edit-category" className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
              </div>
              <Field label="Tags (comma separated)"><input data-testid="edit-tags" className="inp" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Field>
              <Field label="Description"><textarea data-testid="edit-description" rows={5} className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            </div>
            <button data-testid="save-draft" onClick={save} disabled={busy} className="mt-5 inline-flex items-center gap-2 bg-[#3B1F6E] hover:bg-[#2E1065] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
              <Save className="w-4 h-4" /> Save Draft
            </button>
          </div>

          {/* Channels */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-[#0A0F1D] mb-4">Channel Status</h2>
            <div className="space-y-3">
              {CHANNELS.map((c) => {
                const st = p.channels?.[c.key] || {};
                return (
                  <div key={c.key} className="flex items-center justify-between border border-slate-100 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" data-testid={`channel-select-${c.key}`} checked={sel.includes(c.key)}
                        onChange={(e) => setSel(e.target.checked ? [...sel, c.key] : sel.filter((x) => x !== c.key))} className="w-4 h-4 accent-[#3B1F6E]" />
                      <div>
                        <p className="font-medium text-sm text-[#0A0F1D]">{c.label}</p>
                        <p className={`text-xs ${CH_COLOR[st.status] || "text-slate-400"}`} data-testid={`channel-status-${c.key}`}>{(st.status || "pending").replace(/_/g, " ")}{st.last_error ? ` — ${st.last_error}` : ""}</p>
                      </div>
                    </div>
                    {["manual_action_required", "failed"].includes(st.status) && (
                      <button data-testid={`retry-${c.key}`} onClick={() => retry(c.key)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3B1F6E] hover:underline"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>
                    )}
                    {st.public_url && <a href={st.public_url} target="_blank" rel="noreferrer" className="text-xs text-[#D97706] font-semibold">View</a>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions + Preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-[#0A0F1D] mb-1">Publishing</h2>
            <p className="text-xs text-slate-500 mb-4">Customer Ready: <span className={p.customer_ready ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{p.customer_ready ? "Yes" : "No"}</span></p>
            <ConfirmButton testid="publish-button" disabled={!valid || !p.customer_ready} onConfirm={publish}
              title="Approve & Publish?" desc={`This will publish "${p.title}" to: ${sel.join(", ")}. First publication requires Founder approval.`}
              className="w-full bg-[#D97706] hover:bg-[#B45309] text-white" icon={<Rocket className="w-4 h-4" />} label="Approve & Publish" />
            <ConfirmButton testid="unpublish-button" onConfirm={unpublish}
              title="Unpublish product?" desc="The product will be removed from public sale. Records and history are preserved."
              className="w-full mt-3 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" icon={<EyeOff className="w-4 h-4" />} label="Unpublish" />
            <ConfirmButton testid="archive-button" onConfirm={archive}
              title="Archive product?" desc="The product will be hidden but all records and files are retained."
              className="w-full mt-3 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" icon={<Archive className="w-4 h-4" />} label="Archive" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-[#0A0F1D] mb-4 flex items-center gap-2"><Eye className="w-4 h-4 text-[#3B1F6E]" /> Customer Preview</h2>
            <div className="rounded-xl overflow-hidden border border-slate-100">
              <div className="aspect-[3/4] bg-gradient-to-br from-[#3B1F6E] to-[#0A0F1D]">
                {p.cover ? <img src={`${assetUrl(`/api/assets/${productKey}/cover`)}`} alt="cover" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="p-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#D97706]">{form.category}</span>
                <p className="font-serif-display text-lg font-semibold text-[#0A0F1D] mt-1">{form.title}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-3">{form.description}</p>
                <p className="font-bold text-[#2E1065] mt-2">${parseFloat(form.price || 0).toFixed(2)}</p>
              </div>
            </div>
            {p.lifecycle_status === "LIVE" && (
              <a href={`/product/${productKey}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm text-[#3B1F6E] font-semibold"><CheckCircle2 className="w-4 h-4" /> Live on storefront</a>
            )}
          </div>
        </div>
      </div>
      <style>{`.inp{width:100%;height:auto;min-height:42px;border:1px solid #E2E8F0;border-radius:8px;padding:9px 12px;font-size:14px;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(59,31,110,.15);border-color:#3B1F6E}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs font-semibold text-slate-500 mb-1.5">{label}</label>{children}</div>;
}

function ConfirmButton({ testid, disabled, onConfirm, title, desc, className, icon, label }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={testid} disabled={disabled} className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>{icon} {label}</button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{desc}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-[#3B1F6E] hover:bg-[#2E1065]">Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
